import React, { Component } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Badge,
  Table,
  InputGroup
} from 'reactstrap';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';

// Кастомные стили для бейджей и кнопок
const customStyles = {
  badge: {
    active: {
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none'
    },
    inactive: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none'
    },
    category: {
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      marginRight: '4px',
      marginBottom: '4px'
    }
  },
  button: {
    activate: {
      backgroundColor: 'transparent',
      borderColor: '#28a745',
      color: '#28a745'
    },
    deactivate: {
      backgroundColor: 'transparent',
      borderColor: '#fd7e14',
      color: '#fd7e14'
    },
    edit: {
      backgroundColor: 'transparent',
      borderColor: '#007bff',
      color: '#007bff'
    },
    delete: {
      backgroundColor: 'transparent',
      borderColor: '#dc3545',
      color: '#dc3545'
    }
  }
};

// Wrapper component to use hooks in class component
const EmployeeManagementWithAuth = (props) => {
  const auth = useAuth();
  return (
    <ProtectedRoute requiredRoles={["Manager", "Admin"]}>
      <EmployeeManagement {...props} auth={auth} />
    </ProtectedRoute>
  );
};

class EmployeeManagement extends Component {
  static displayName = EmployeeManagement.name;

  constructor(props) {
    super(props);
    this.state = {
      employees: [],
      workCategories: [],
      qualifications: [],
      qualificationsByCategory: {},
      loading: true,
      error: null,
      createModal: false,
      editModal: false,
      qualificationsModal: false,
      selectedEmployee: null,
      searchTerm: '',
      qualificationSearchTerm: '',
      formData: {
        firstName: '',
        lastName: '',
        middleName: '',
        phone: '',
        hireDate: '',
        workCategoryIds: [],
        qualificationIds: [],
        selectedQualifications: {}, // { categoryId: qualificationId }
        notes: ''
      },
      formErrors: {}
    };
  }

  getTariffSuffixByCategoryId = (workCategoryId) => {
    const category = this.state.workCategories?.find(c => c.id === workCategoryId);
    if (category?.unit === 2) return '₽/шт';
    if (category?.unit === 1) return '₽';
    return '₽/ч';
  };

  async componentDidMount() {
    await this.loadEmployees();
    await this.loadWorkCategories();
    await this.loadQualifications();
  }

  loadEmployees = async () => {
    try {
      this.setState({ loading: true });
      const response = await this.props.auth.authenticatedFetch('/api/employees?includeInactive=true');

      if (response && response.ok) {
        const employees = await response.json();
        this.setState({ employees, loading: false });
      } else if (response) {
        this.setState({ error: 'Не удалось загрузить сотрудников', loading: false });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети', loading: false });
    }
  };

  loadWorkCategories = async () => {
    try {
      const response = await this.props.auth.authenticatedFetch('/api/workcategories');

      if (response && response.ok) {
        const categories = await response.json();
        this.setState({ workCategories: categories });
      }
    } catch (error) {
      console.error('Failed to load work categories:', error);
    }
  };

  loadQualifications = async () => {
    try {
      const response = await this.props.auth.authenticatedFetch('/api/qualifications');

      if (response && response.ok) {
        const qualifications = await response.json();

        // Group qualifications by work category
        const qualificationsByCategory = {};
        qualifications.forEach(qual => {
          if (!qualificationsByCategory[qual.workCategoryId]) {
            qualificationsByCategory[qual.workCategoryId] = [];
          }
          qualificationsByCategory[qual.workCategoryId].push(qual);
        });

        this.setState({
          qualifications,
          qualificationsByCategory
        });
      }
    } catch (error) {
      console.error('Failed to load qualifications:', error);
    }
  };

  openCreateModal = () => {
    this.setState({
      createModal: true,
      formData: {
        firstName: '',
        lastName: '',
        middleName: '',
        phone: '',
        hireDate: '',
        workCategoryIds: [],
        qualificationIds: [],
        selectedQualifications: {},
        notes: ''
      },
      formErrors: {}
    });
  };

  openEditModal = (employee) => {
    // Build selectedQualifications object from employee's qualifications
    const selectedQualifications = {};
    if (employee.qualifications) {
      employee.qualifications.forEach(qual => {
        selectedQualifications[qual.workCategoryId] = qual.id;
      });
    }

    this.setState({
      editModal: true,
      selectedEmployee: employee,
      formData: {
        firstName: employee.firstName,
        lastName: employee.lastName,
        middleName: employee.middleName || '',
        phone: employee.phone || '',
        hireDate: employee.hireDate ? employee.hireDate.split('T')[0] : '',
        workCategoryIds: employee.workCategoryIds || [],
        qualificationIds: employee.qualificationIds || [],
        selectedQualifications: selectedQualifications,
        notes: employee.notes || ''
      },
      formErrors: {}
    });
  };

  closeModals = () => {
    this.setState({
      createModal: false,
      editModal: false,
      linkModal: false,
      selectedEmployee: null,
      formErrors: {}
    });
  };

  showEmployeeLink = (employee) => {
    this.setState({
      linkModal: true,
      selectedEmployee: employee
    });
  };

  generateEmployeeToken = async (employeeId) => {
    try {
      const response = await this.props.auth.authenticatedFetch(`/api/employees/${employeeId}/regenerate-token`, {
        method: 'POST'
      });

      if (response && response.ok) {
        await this.loadEmployees();
        this.setState({ successMessage: 'Токен сгенерирован успешно' });
        setTimeout(() => this.setState({ successMessage: null }), 3000);
      } else {
        this.setState({ error: 'Не удалось сгенерировать токен' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ successMessage: 'Ссылка скопирована в буфер обмена' });
      setTimeout(() => this.setState({ successMessage: null }), 3000);
    });
  };

  openQualificationsModal = () => {
    this.setState({
      qualificationsModal: true,
      qualificationSearchTerm: ''
    });
  };

  closeQualificationsModal = () => {
    this.setState({
      qualificationsModal: false,
      qualificationSearchTerm: ''
    });
  };

  saveQualifications = () => {
    this.closeQualificationsModal();
  };

  handleInputChange = (e) => {
    const { name, value, type, options } = e.target;

    if (name === 'workCategoryIds') {
      const selectedValues = Array.from(options)
        .filter(option => option.selected)
        .map(option => option.value);

      this.setState({
        formData: {
          ...this.state.formData,
          [name]: selectedValues
        }
      });
    } else {
      this.setState({
        formData: {
          ...this.state.formData,
          [name]: value
        }
      });
    }
  };

  handleQualificationChange = (categoryId, qualificationId) => {
    const { selectedQualifications } = this.state.formData;
    const { qualifications } = this.state;
    const newSelectedQualifications = { ...selectedQualifications };

    // Find the selected qualification
    const selectedQualification = qualifications.find(q => q.id === qualificationId);

    if (selectedQualification && selectedQualification.name === 'По умолчанию') {
      // If "По умолчанию" is selected, remove the qualification (don't save default)
      delete newSelectedQualifications[categoryId];
    } else if (qualificationId) {
      // If non-default qualification is selected, save it
      newSelectedQualifications[categoryId] = qualificationId;
    }

    // Convert selectedQualifications object to array for backend
    const qualificationIds = Object.values(newSelectedQualifications);

    this.setState({
      formData: {
        ...this.state.formData,
        selectedQualifications: newSelectedQualifications,
        qualificationIds: qualificationIds
      }
    });
  };

  validateForm = () => {
    const { formData } = this.state;
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'Имя обязательно';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Фамилия обязательна';
    }

    this.setState({ formErrors: errors });
    return Object.keys(errors).length === 0;
  };

  saveEmployee = async () => {
    if (!this.validateForm()) return;

    try {
      const { formData, selectedEmployee } = this.state;
      const isEdit = !!selectedEmployee;
      
      const requestData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        middleName: formData.middleName || null,
        phone: formData.phone || null,
        hireDate: formData.hireDate || null,
        workCategoryIds: formData.workCategoryIds,
        qualificationIds: formData.qualificationIds,
        notes: formData.notes || null
      };

      const url = isEdit ? `/api/employees/${selectedEmployee.id}` : '/api/employees';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await this.props.auth.authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (response && response.ok) {
        await this.loadEmployees();
        this.closeModals();
      } else if (response) {
        const errorData = await response.json();
        this.setState({ error: errorData.message || 'Не удалось сохранить сотрудника' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  deleteEmployee = async (employeeId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого сотрудника?')) {
      return;
    }

    try {
      const response = await this.props.auth.authenticatedFetch(`/api/employees/${employeeId}`, {
        method: 'DELETE'
      });

      if (response && response.ok) {
        await this.loadEmployees();
      } else if (response) {
        this.setState({ error: 'Не удалось удалить сотрудника' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  toggleEmployeeStatus = async (employeeId) => {
    try {
      const response = await this.props.auth.authenticatedFetch(`/api/employees/${employeeId}/toggle-status`, {
        method: 'POST'
      });

      if (response && response.ok) {
        await this.loadEmployees();
      } else if (response) {
        this.setState({ error: 'Не удалось изменить статус сотрудника' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  handleSearch = async () => {
    const { searchTerm } = this.state;
    
    if (!searchTerm.trim()) {
      await this.loadEmployees();
      return;
    }

    try {
      const response = await this.props.auth.authenticatedFetch(`/api/employees/search?searchTerm=${encodeURIComponent(searchTerm)}&includeInactive=true`);
      
      if (response && response.ok) {
        const employees = await response.json();
        this.setState({ employees });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка поиска' });
    }
  };

  clearSearch = async () => {
    this.setState({ searchTerm: '' });
    await this.loadEmployees();
  };

  formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  renderSelectedQualificationsSummary = () => {
    const { selectedQualifications } = this.state.formData;
    const { qualifications } = this.state;

    const selectedQualificationIds = Object.values(selectedQualifications);
    const selectedQualificationObjects = qualifications.filter(q =>
      selectedQualificationIds.includes(q.id)
    );

    if (selectedQualificationObjects.length === 0) {
      return <span className="text-muted">Только стандартные квалификации</span>;
    }

    return (
      <div>
        <span className="text-success fw-bold">
          Специальных: {selectedQualificationObjects.length}
        </span>
        <div className="mt-1">
          {selectedQualificationObjects.map(qual => (
            <Badge
              key={qual.id}
              style={customStyles.badge.category}
              className="me-1 mb-1"
            >
              {qual.name}
            </Badge>
          ))}
        </div>
        <small className="text-muted d-block mt-1">
          Остальные виды работ используют стандартные квалификации
        </small>
      </div>
    );
  };

  renderQualificationSelection = () => {
    const { qualificationsByCategory, qualificationSearchTerm } = this.state;
    const { selectedQualifications } = this.state.formData;

    // Filter categories that have more than one qualification
    let categoriesWithMultipleQualifications = Object.keys(qualificationsByCategory)
      .filter(categoryId => qualificationsByCategory[categoryId].length > 1)
      .map(categoryId => {
        const category = this.state.workCategories.find(cat => cat.id === categoryId);
        return {
          id: categoryId,
          name: category?.name || 'Unknown Category',
          qualifications: qualificationsByCategory[categoryId]
        };
      });

    // Apply search filter
    if (qualificationSearchTerm) {
      categoriesWithMultipleQualifications = categoriesWithMultipleQualifications.filter(category =>
        category.name.toLowerCase().includes(qualificationSearchTerm.toLowerCase()) ||
        category.qualifications.some(qual =>
          qual.name.toLowerCase().includes(qualificationSearchTerm.toLowerCase())
        )
      );
    }

    if (categoriesWithMultipleQualifications.length === 0) {
      return (
        <div className="text-muted text-center py-4">
          {qualificationSearchTerm ? (
            <p>Ничего не найдено по запросу "{qualificationSearchTerm}"</p>
          ) : (
            <>
              <p>Нет видов работ с несколькими квалификациями.</p>
              <small>Квалификации назначаются только для видов работ, у которых есть несколько уровней квалификации.</small>
            </>
          )}
        </div>
      );
    }

    return (
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {categoriesWithMultipleQualifications.map(category => (
          <div key={category.id} className="mb-3 p-3 border rounded">
            <Label className="fw-bold mb-2 d-flex align-items-center">
              📁 {category.name}
              {selectedQualifications[category.id] && (
                <Badge color="success" className="ms-2">Выбрано</Badge>
              )}
            </Label>
            <div>
              {category.qualifications.map(qualification => {
                const isDefault = qualification.name === 'По умолчанию';
                const isSelected = selectedQualifications[category.id] === qualification.id;
                const isDefaultSelected = !selectedQualifications[category.id] && isDefault;

                return (
                  <div key={qualification.id} className="form-check mb-2">
                    <Input
                      type="radio"
                      name={`qualification_${category.id}`}
                      id={`qual_${qualification.id}`}
                      checked={isSelected || isDefaultSelected}
                      onChange={() => this.handleQualificationChange(category.id, qualification.id)}
                      className="form-check-input"
                    />
                    <Label for={`qual_${qualification.id}`} className="form-check-label">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <strong>
                            {qualification.name}
                            {isDefault && <span className="text-muted ms-1">(стандартная)</span>}
                          </strong>
                          {qualification.description && (
                            <div className="text-muted small">{qualification.description}</div>
                          )}
                        </div>
                      <Badge
                          color={isDefault ? "secondary" : "success"}
                          className="ms-2"
                        >
                          {qualification.tariff?.toLocaleString()} {this.getTariffSuffixByCategoryId(category.id)}
                        </Badge>
                      </div>
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  renderEmployeeForm = () => {
    const { formData, formErrors, workCategories } = this.state;

    return (
      <Form>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="firstName">Имя *</Label>
              <Input
                type="text"
                name="firstName"
                id="firstName"
                value={formData.firstName}
                onChange={this.handleInputChange}
                invalid={!!formErrors.firstName}
                placeholder="Например: Иван"
              />
              {formErrors.firstName && <div className="invalid-feedback">{formErrors.firstName}</div>}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="lastName">Фамилия *</Label>
              <Input
                type="text"
                name="lastName"
                id="lastName"
                value={formData.lastName}
                onChange={this.handleInputChange}
                invalid={!!formErrors.lastName}
                placeholder="Например: Петров"
              />
              {formErrors.lastName && <div className="invalid-feedback">{formErrors.lastName}</div>}
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label for="middleName">Отчество</Label>
          <Input
            type="text"
            name="middleName"
            id="middleName"
            value={formData.middleName}
            onChange={this.handleInputChange}
            placeholder="Например: Сергеевич"
          />
        </FormGroup>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="phone">Телефон</Label>
              <Input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={this.handleInputChange}
                placeholder="+7 (999) 123-45-67"
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="hireDate">Дата рождения</Label>
              <Input
                type="date"
                name="hireDate"
                id="hireDate"
                value={formData.hireDate}
                onChange={this.handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label>🎯 Квалификации сотрудника</Label>
          <div className="d-flex align-items-center">
            <Button
              color="outline-primary"
              onClick={this.openQualificationsModal}
              className="me-3"
            >
              ⚙️ Настроить квалификации
            </Button>
            {this.renderSelectedQualificationsSummary()}
          </div>
        </FormGroup>

        <FormGroup>
          <Label for="notes">Заметки</Label>
          <Input
            type="textarea"
            name="notes"
            id="notes"
            value={formData.notes}
            onChange={this.handleInputChange}
            rows="3"
            placeholder="Дополнительная информация о сотруднике..."
          />
        </FormGroup>
      </Form>
    );
  };

  render() {
    const {
      employees,
      workCategories,
      loading,
      error,
      createModal,
      editModal,
      linkModal,
      selectedEmployee,
      formData,
      formErrors,
      searchTerm
    } = this.state;

    if (loading) {
      return (
        <Container className="mt-5">
          <div className="text-center">Загрузка сотрудников...</div>
        </Container>
      );
    }

    return (
      <Container className="mt-5">
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">👥 Сотрудники</h3>
                <Button color="primary" onClick={this.openCreateModal}>
                  ➕ Добавить сотрудника
                </Button>
              </CardHeader>
              <CardBody>
                {error && <Alert color="danger">{error}</Alert>}
                
                {/* Search */}
                <Row className="mb-3">
                  <Col md={6}>
                    <InputGroup>
                      <Input
                        type="text"
                        placeholder="Поиск по имени, фамилии или телефону..."
                        value={searchTerm}
                        onChange={(e) => this.setState({ searchTerm: e.target.value })}
                        onKeyPress={(e) => e.key === 'Enter' && this.handleSearch()}
                      />
                      <Button color="outline-secondary" onClick={this.handleSearch}>
                        🔍 Поиск
                      </Button>
                      {searchTerm && (
                        <Button color="outline-danger" onClick={this.clearSearch}>
                          ✖️ Очистить
                        </Button>
                      )}
                    </InputGroup>
                  </Col>
                </Row>

                {employees.length === 0 ? (
                  <div className="text-center text-muted">
                    {searchTerm ? 'Сотрудники не найдены' : 'Нет сотрудников'}
                  </div>
                ) : (
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>👤 ФИО</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>📞 Телефон</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>🎂 Дата рождения</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>🎯 Квалификации</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>🔄 Статус</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>🔗 Ссылка</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white', width: '200px' }}>⚙️ Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map(employee => (
                        <tr key={employee.id}>
                          <td>
                            <div>
                              <strong>{employee.fullName}</strong>
                            </div>
                            {employee.notes && (
                              <small className="text-muted">{employee.notes}</small>
                            )}
                          </td>
                          <td>{employee.phone || '—'}</td>
                          <td>{this.formatDate(employee.hireDate)}</td>
                          <td>
                            {employee.qualifications && employee.qualifications.length > 0 ? (
                              employee.qualifications.map(qualification => (
                                <div key={qualification.id} className="mb-1">
                                  <Badge
                                    style={customStyles.badge.category}
                                    className="me-1"
                                  >
                                    {qualification.name}
                                  </Badge>
                                  <small className="text-muted">
                                    ({qualification.tariff?.toLocaleString()} {this.getTariffSuffixByCategoryId(qualification.workCategoryId)})
                                  </small>
                                </div>
                              ))
                            ) : (
                              <span className="text-muted">Не назначены</span>
                            )}
                          </td>
                          <td>
                            <Badge
                              style={employee.isActive ? customStyles.badge.active : customStyles.badge.inactive}
                            >
                              {employee.isActive ? 'Активен' : 'Неактивен'}
                            </Badge>
                          </td>
                          <td>
                            {employee.accessToken ? (
                              <Button
                                size="sm"
                                color="info"
                                onClick={() => this.showEmployeeLink(employee)}
                                title="Показать ссылку для сотрудника"
                              >
                                🔗 Ссылка
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                color="secondary"
                                onClick={() => this.generateEmployeeToken(employee.id)}
                                title="Сгенерировать токен"
                              >
                                🔗 Создать
                              </Button>
                            )}
                          </td>
                          <td>
                            <div className="btn-group" role="group">
                              <Button
                                size="sm"
                                style={customStyles.button.edit}
                                onClick={() => this.openEditModal(employee)}
                              >
                                ✏️ Изменить
                              </Button>
                              <Button
                                size="sm"
                                style={employee.isActive ? customStyles.button.deactivate : customStyles.button.activate}
                                onClick={() => this.toggleEmployeeStatus(employee.id)}
                              >
                                {employee.isActive ? '⏸️ Деактивировать' : '▶️ Активировать'}
                              </Button>
                              <Button
                                size="sm"
                                style={customStyles.button.delete}
                                onClick={() => this.deleteEmployee(employee.id)}
                              >
                                🗑️ Удалить
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Create/Edit Modal */}
        <Modal isOpen={createModal || editModal} toggle={this.closeModals} size="lg">
          <ModalHeader toggle={this.closeModals}>
            {createModal ? 'Добавить сотрудника' : 'Редактировать сотрудника'}
          </ModalHeader>
          <ModalBody>
            {this.renderEmployeeForm()}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.saveEmployee}>
              {createModal ? 'Создать' : 'Сохранить'}
            </Button>
            <Button color="secondary" onClick={this.closeModals}>
              Отмена
            </Button>
          </ModalFooter>
        </Modal>

        {/* Qualifications Modal */}
        <Modal isOpen={this.state.qualificationsModal} toggle={this.closeQualificationsModal} size="lg">
          <ModalHeader toggle={this.closeQualificationsModal}>
            🎯 Настройка квалификаций сотрудника
          </ModalHeader>
          <ModalBody>
            {/* Search */}
            <FormGroup>
              <Label for="qualificationSearch">🔍 Поиск по видам работ или квалификациям</Label>
              <Input
                type="text"
                id="qualificationSearch"
                placeholder="Введите название..."
                value={this.state.qualificationSearchTerm}
                onChange={(e) => this.setState({ qualificationSearchTerm: e.target.value })}
              />
            </FormGroup>

            <hr />

            <div className="mb-3">
              <small className="text-muted">
                Выберите квалификацию для каждого вида работ. Квалификация "По умолчанию" означает стандартный уровень (не сохраняется).
                Показаны только виды работ с несколькими квалификациями.
              </small>
            </div>

            {this.renderQualificationSelection()}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.saveQualifications}>
              ✅ Сохранить квалификации
            </Button>
            <Button color="secondary" onClick={this.closeQualificationsModal}>
              Отмена
            </Button>
          </ModalFooter>
        </Modal>

        {/* Link Modal */}
        <Modal isOpen={linkModal} toggle={this.closeModals}>
          <ModalHeader toggle={this.closeModals}>
            🔗 Ссылка для сотрудника
          </ModalHeader>
          <ModalBody>
            {selectedEmployee && (
              <div>
                <p><strong>Сотрудник:</strong> {selectedEmployee.fullName}</p>
                <p><strong>Ссылка для заполнения работ:</strong></p>
                <InputGroup>
                  <Input
                    type="text"
                    value={`${window.location.origin}/employee/${selectedEmployee.accessToken}`}
                    readOnly
                  />
                  <Button
                    color="primary"
                    onClick={() => this.copyToClipboard(`${window.location.origin}/employee/${selectedEmployee.accessToken}`)}
                  >
                    📋 Копировать
                  </Button>
                </InputGroup>
                <small className="text-muted mt-2 d-block">
                  Отправьте эту ссылку сотруднику для заполнения отчетов о выполненных работах.
                </small>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="warning" onClick={() => this.generateEmployeeToken(selectedEmployee?.id)}>
              🔄 Обновить токен
            </Button>
            <Button color="secondary" onClick={this.closeModals}>
              Закрыть
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    );
  }
}

export default EmployeeManagementWithAuth;
